import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Helmet } from 'react-helmet-async';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { FileText, Download, Briefcase, FileArchive, Search, Filter, Flame } from 'lucide-react';
import { Link } from 'react-router-dom';

const RESOURCES = [
  {
    id: 1,
    wordCount: 3650,
    title: 'The Cost of Executive Misalignment',
    description: 'Why leadership teams that agree on paper drift apart in practice, what the published research shows, and a twelve-question audit to find where misalignment is costing you.',
    category: 'Whitepapers',
    icon: FileText,
    fileSize: '0.7 MB',
    type: 'PDF',
    url: '/resource-library/the-cost-of-executive-misalignment',
    date: 'Nov 2026',
    featured: false,
    trending: true
  },
  {
    id: 2,
    wordCount: 1330,
    title: 'Transforming Global Logistics: An Illustrative Scenario',
    description: 'An illustrative scenario: how a newly combined logistics leadership team could use the FIVE LENS framework to work through departmental friction and slow decisions.',
    category: 'Scenarios',
    icon: Briefcase,
    fileSize: '0.6 MB',
    type: 'PDF',
    url: '/resource-library/transforming-global-logistics',
    date: 'Nov 2026',
    featured: false,
    trending: false
  },
  {
    id: 3,
    wordCount: 2550,
    title: 'Personal Mastery Assessment Toolkit',
    description: 'Self-reflection worksheets for leaders, coaches and HR professionals to baseline personal mastery and emotional regulation, with a 30-day development plan.',
    category: 'Toolkits',
    icon: FileArchive,
    fileSize: '0.7 MB',
    type: 'PDF',
    url: '/resource-library/personal-mastery-assessment-toolkit',
    date: 'Nov 2026',
    featured: false,
    trending: true
  },
  {
    id: 4,
    wordCount: 5700,
    title: 'Navigating Change Fatigue',
    description: 'Actionable strategies for maintaining emotional resilience across enterprise teams during large-scale digital transformations and M&A integrations.',
    category: 'Whitepapers',
    icon: FileText,
    fileSize: '0.7 MB',
    type: 'PDF',
    url: '/resource-library/navigating-change-fatigue',
    date: 'Oct 2026',
    featured: true,
    trending: false
  },
  {
    id: 5,
    wordCount: 1530,
    title: 'Healthcare Systems Optimisation: An Illustrative Scenario',
    description: 'An illustrative scenario: how the Energy Centres lens can help a multidisciplinary clinical team understand recurring friction, what to try and what to measure.',
    category: 'Scenarios',
    icon: Briefcase,
    fileSize: '0.6 MB',
    type: 'PDF',
    url: '/resource-library/healthcare-systems-optimisation',
    date: 'Nov 2026',
    featured: true,
    trending: false
  },
  {
    id: 6,
    wordCount: 2330,
    title: 'Team Alignment Workshop Guide',
    description: 'Step-by-step facilitation guide for running an introductory FIVE LENS alignment workshop with newly formed leadership teams.',
    category: 'Toolkits',
    icon: FileArchive,
    fileSize: '0.7 MB',
    type: 'PDF',
    url: '/resource-library/team-alignment-workshop-guide',
    date: 'Nov 2026',
    featured: false,
    trending: false
  }
];

import { searchIndex } from '@/lib/search';

const CATEGORIES = ['All', 'Whitepapers', 'Scenarios', 'Toolkits'];

export function ResourceLibraryPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const categoryFiltered = RESOURCES.filter(resource => 
    activeCategory === 'All' || resource.category === activeCategory
  );
  
  const filteredResources = searchIndex(categoryFiltered, searchQuery, ['title', 'description', 'category']);


  return (
    <main className="pt-32 pb-24 min-h-screen bg-ground">
      <Helmet>
        <title>Resource Library | Guides & Tools | FIVE LENS Enneagram</title>
        <meta name="description" content="Downloadable guides, research papers, and practical tools for coaches and HR professionals using the Enneagram in organisations." />
        <meta property="og:title" content="Resource Library | Guides & Tools | FIVE LENS Enneagram" />
        <meta property="og:description" content="Downloadable guides, research papers, and practical tools for coaches and HR professionals using the Enneagram in organisations." />
        <meta name="twitter:title" content="Resource Library | Guides & Tools | FIVE LENS Enneagram" />
        <meta name="twitter:description" content="Downloadable guides, research papers, and practical tools for coaches and HR professionals using the Enneagram in organisations." />
      </Helmet>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Breadcrumb items={[{ label: 'Resource Library' }]} />
        
        <div className="mb-12 mt-8">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-rule-3 mb-4 block">Insights & Downloads</span>
          <h1 className="text-4xl lg:text-5xl font-serif text-ink mb-6">Resource Library</h1>
          <p className="text-xl text-ink/70/80 max-w-3xl leading-relaxed font-light">
            Access our collection of research, case studies, and practical tools designed to help organizations build high-trust cultures and align executive teams.
          </p>
        </div>

        {/* Featured Resources Section */}
        {activeCategory === 'All' && searchQuery === '' && (
          <div className="mb-16">
            <h2 className="text-2xl font-serif text-ink mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-rule-3"></span>
              Featured Resources
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {RESOURCES.filter(r => r.featured).map((resource, index) => (
                <motion.div 
                  key={`featured-${resource.id}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-ink text-white border border-ink flex flex-col hover:shadow-lg hover:shadow-ink/20 hover:-translate-y-1 hover:border-rule-3 transition-all group relative rounded-sm overflow-hidden"
                >
                  <div className="absolute top-0 right-0 w-32 h-32 bg-rule-3/20 rounded-bl-full -mr-8 -mt-8 transition-transform group-hover:scale-110"></div>
                  <div className="p-8 flex-grow relative z-10">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-ground bg-white/10 px-3 py-1 rounded-full border border-white/20">
                          {resource.category}
                        </span>
                        {resource.trending && (
                          <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-white bg-rule-3/80 px-3 py-1 rounded-full shadow-sm">
                            <Flame className="w-3 h-3" /> Trending
                          </span>
                        )}
                      </div>
                      <resource.icon className="w-6 h-6 text-ground/60" />
                    </div>
                    <h3 className="text-2xl font-serif text-white mb-4 group-hover:text-ground transition-colors">
                      {resource.title}
                    </h3>
                    <p className="text-sm text-ground/80 leading-relaxed font-light mb-6">
                      {resource.description}
                    </p>
                  </div>
                  <div className="p-6 border-t border-white/10 bg-black/20 flex items-center justify-between mt-auto group-hover:bg-rule-3 transition-colors relative z-10">
                    <div className="flex items-center gap-2 text-xs font-medium text-ground/70 group-hover:text-white transition-colors">
                      <span className="uppercase tracking-wider">{resource.type}</span>
                      <span>•</span>
                      <span>{resource.fileSize}</span>
                      {resource.date && (
                        <>
                          <span>•</span>
                          <span>{resource.date}</span>
                        </>
                      )}
                      {resource.wordCount && (
                        <>
                          <span>•</span>
                          <span>{Math.ceil(resource.wordCount / 225)} min read</span>
                        </>
                      )}
                    </div>
                    <Link 
                      to={resource.url}
                      className="text-xs font-bold uppercase tracking-widest text-ground group-hover:text-white transition-colors before:absolute before:inset-0"
                      aria-label={`Open ${resource.title}`}
                    >
                      Open
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col md:flex-row gap-6 mb-12">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-ink/70/50" />
            <input 
              type="text" 
              placeholder="Search resources..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-ground-deep bg-white focus:outline-none focus:border-rule-3 text-sm transition-colors"
            />
          </div>

          {/* Categories */}
          <div className="flex flex-wrap items-center gap-2">
            <Filter className="w-4 h-4 text-ink/70 mr-2" />
            {CATEGORIES.map(category => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-sm transition-colors ${
                  activeCategory === category 
                    ? 'bg-ink text-white' 
                    : 'bg-white border border-ground-deep text-ink hover:border-rule-3'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredResources.map((resource, index) => (
            <motion.div 
              key={resource.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white border border-ground-deep flex flex-col hover:shadow-md hover:shadow-ink/5 hover:-translate-y-1 transition-all group relative"
            >
              <div className="p-8 flex-grow">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-rule-3 bg-ground px-3 py-1 rounded-full">
                      {resource.category}
                    </span>
                    {resource.trending && (
                      <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-white bg-rule-3 px-3 py-1 rounded-full shadow-sm">
                        <Flame className="w-3 h-3" /> Popular
                      </span>
                    )}
                  </div>
                  <resource.icon className="w-6 h-6 text-ink/70/40" />
                </div>
                <h3 className="text-xl font-serif text-ink mb-4 group-hover:text-rule-3 transition-colors">
                  {resource.title}
                </h3>
                <p className="text-sm text-ink/70/80 leading-relaxed font-light mb-6">
                  {resource.description}
                </p>
              </div>
              <div className="p-6 border-t border-ground-deep bg-ground-alt/30 flex items-center justify-between mt-auto group-hover:bg-ink transition-colors">
                <div className="flex items-center gap-2 text-xs font-medium text-ink/70 group-hover:text-ground-deep transition-colors">
                  <span className="uppercase tracking-wider">{resource.type}</span>
                  <span>•</span>
                  <span>{resource.fileSize}</span>
                  {resource.date && (
                    <>
                      <span>•</span>
                      <span>{resource.date}</span>
                    </>
                  )}
                  {resource.wordCount && (
                    <>
                      <span>•</span>
                      <span>{Math.ceil(resource.wordCount / 225)} min read</span>
                    </>
                  )}
                </div>
                <Link 
                    to={resource.url}
                    className="text-xs font-bold uppercase tracking-widest text-ink group-hover:text-white transition-colors before:absolute before:inset-0"
                    aria-label={`Open ${resource.title}`}
                  >
                    Open
                  </Link>
              </div>
            </motion.div>
          ))}
        </div>
        
        {filteredResources.length === 0 && (
          <div className="py-20 text-center text-ink/70">
            <p className="text-lg mb-2">No resources found matching your search.</p>
            <button onClick={() => {setSearchQuery(''); setActiveCategory('All');}} className="text-rule-3 font-bold hover:underline text-sm uppercase tracking-wider">Clear Filters</button>
          </div>
        )}
      </div>
    </main>
  );
}
